import {sharedCampaigns} from '@cl/shared';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {
  DTCreativeNode,
  DTNativeCreativeNode,
  CreativeNodePlaceholderCreativeId,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {CLStore} from 'common/src/redux/store';
import {CampaignType} from 'common/src/modules/campaigns/campaigns.types';
import {LandingPageSource} from 'common/src/modules/campaigns/campaigns.enums';
import {MINIMUM_NATIVE_LOGO_SIZE} from 'common/src/modules/creatives/creatives.types';

function createDefaultDTCreativeCard(): DTCreativeNode {
  return {
    id: NO_NODE_ID,
    name: 'Creative Card',
    description: '',
    type: NodeType.CREATIVE,
    outputs: [],
    inputs: [],
    creativeId: CreativeNodePlaceholderCreativeId,
    creative: undefined as unknown as DTCreativeNode['creative'],
    landingPageSource: LandingPageSource.Campaign,
  };
}

function createDefaultDTNativeCreativeCard(): DTNativeCreativeNode {
  const baseCard = createDefaultDTCreativeCard();

  return {
    ...baseCard,
    type: NodeType.NATIVE_CREATIVE,
    nativeAdTitle: '',
    nativeAdDescription: '',
    nativeAdSponsor: '',
    nativeAdCTA: '',
    nativeAdLogo: '',
    nativeAdLogoSize: MINIMUM_NATIVE_LOGO_SIZE,
  };
}

function createDefaultCreativeCard(
  nodeType: NodeType.CREATIVE | NodeType.NATIVE_CREATIVE,
): DTCreativeNode | DTNativeCreativeNode {
  return nodeType === NodeType.NATIVE_CREATIVE
    ? createDefaultDTNativeCreativeCard()
    : createDefaultDTCreativeCard();
}

/**
 * Gets an existing creative card by ID, or creates a new default one.
 * For Native Ads campaigns creates DTNativeCreativeNode; for others creates DTCreativeNode.
 * Uses structuredClone to create a mutable copy.
 * @param cardId - The card ID to fetch, or 0/undefined for a new card
 * @param store - Redux store (used to get campaign type)
 * @returns The creative card (existing or new), fully mutable
 */
async function getOrCreateCreativeCard(
  cardId: number | undefined,
  store: CLStore,
): Promise<DTCreativeNode | DTNativeCreativeNode> {
  const campaign = store.getState().campaignPage.campaignTree.campaign;

  if (!campaign) {
    throw new Error('No campaign found');
  }

  const expectedNodeType =
    campaign.type === CampaignType.NativeAds ? NodeType.NATIVE_CREATIVE : NodeType.CREATIVE;
  const resolvedCardId = cardId && cardId > NO_NODE_ID ? cardId : NO_NODE_ID;

  if (resolvedCardId) {
    const existingCard = sharedCampaigns.nodesRef.value[resolvedCardId];

    if (!existingCard) {
      throw new Error(`Card with id ${resolvedCardId} not found`);
    }

    if (existingCard.type !== NodeType.CREATIVE && existingCard.type !== NodeType.NATIVE_CREATIVE) {
      throw new Error(`Card with id ${resolvedCardId} is not a creative card`);
    }

    if (existingCard.type !== expectedNodeType) {
      throw new Error(
        `Card with id ${resolvedCardId} has type ${existingCard.type} but campaign expects ${expectedNodeType}`,
      );
    }

    return structuredClone(existingCard) as DTCreativeNode | DTNativeCreativeNode;
  }

  return createDefaultCreativeCard(expectedNodeType);
}

export default getOrCreateCreativeCard;
export {createDefaultCreativeCard, createDefaultDTCreativeCard, createDefaultDTNativeCreativeCard};
